import { forwardRef, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CountryPhoneSelect from '../CountryPhoneSelect/CountryPhoneSelect';
import {
  createEmptyPhoneModel,
  getPhoneCountry,
  parseCanonicalPhone,
  parsePhoneInput,
} from '../../utils/phoneCountries';
import styles from './phoneinput.module.css';

const PhoneInput = forwardRef(function PhoneInput({
  value,
  onChange,
  defaultCountry = 'BR',
  disabled = false,
  required = false,
  label,
  error = '',
  className = '',
  name,
  onBlur,
}, ref) {
  const { t } = useTranslation();
  const reactId = useId().replace(/:/g, '');
  const inputId = `phone-input-${reactId}`;
  const errorId = `phone-error-${reactId}`;
  const model = useMemo(() => {
    if (value && typeof value === 'object') return value;
    if (typeof value === 'string' && value.trim()) {
      return parseCanonicalPhone(value) || parsePhoneInput(value, defaultCountry);
    }
    return createEmptyPhoneModel(defaultCountry);
  }, [defaultCountry, value]);
  const selectedCountry = useMemo(
    () => getPhoneCountry(model.countryIso || defaultCountry),
    [defaultCountry, model.countryIso]
  );

  const handleInputChange = (event) => {
    onChange?.(parsePhoneInput(event.target.value, model.countryIso || defaultCountry));
  };

  const handleCountryChange = (country) => {
    const next = model.displayValue
      ? parsePhoneInput(model.displayValue, country.iso)
      : createEmptyPhoneModel(country.iso);
    onChange?.({ ...next, countryIso: country.iso });
  };

  return (
    <div className={styles.field}>
      {label && <label className={styles.label} htmlFor={inputId}>{label}</label>}
      <div className={styles.row}>
        <CountryPhoneSelect
          value={selectedCountry}
          onChange={handleCountryChange}
          label={t('auth.country')}
          disabled={disabled}
        />
        <input
          id={inputId}
          ref={ref}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          name={name}
          value={model.displayValue || ''}
          onChange={handleInputChange}
          onBlur={onBlur}
          placeholder={selectedCountry?.placeholder || t('phoneCountry.placeholder')}
          disabled={disabled}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={`${styles.input} ${className}`.trim()}
        />
      </div>
      {error && <span id={errorId} className={styles.error} role="alert">{error}</span>}
    </div>
  );
});

export default PhoneInput;
